import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "formContainer",
    "progressContainer",
    "progressBar",
    "progressText",
    "aggregateCounts",
    "successCount",
    "duplicateCount",
    "failedCount",
    "cardList",
  ]

  static values = {
    uploadUrl: String,
    redirectUrl: String,
    uploadingTemplate: String,
    unzippingTemplate: String,
    completeText: String,
    successTemplate: String,
    duplicateTemplate: String,
    failedTemplate: String,
  }

  connect() {
    this.extractionMode = null
  }

  captureMode(event) {
    this.extractionMode = event.currentTarget.value
  }

  async submit(event) {
    const form = event.target
    const fileInput = form.querySelector('input[type="file"]')
    const files = Array.from(fileInput.files)

    if (files.length === 0) return

    event.preventDefault()
    await this.#runBatchUpload(form, files)
  }

  // Import files picked from Google Drive, reusing the exact same progress UI and
  // redirect as a local upload. Each file is posted to a JSON endpoint that downloads
  // it server-side and returns the same progress card.
  async driveImport(event) {
    event.preventDefault()
    const button = event.currentTarget

    let files = []
    try {
      files = JSON.parse(document.querySelector('[data-drive-import-target="driveFiles"]')?.value || "[]")
    } catch {
      files = []
    }
    if (files.length === 0) return

    const clientId = button.dataset.clientId || document.querySelector(`input[name="${button.dataset.clientField}"]`)?.value || ""
    if (!clientId) return

    const formatId = document.querySelector('select[name="statement[statement_format_id]"]')?.value || ""
    const url = button.dataset.importTemplate.replace("CID", clientId)
    const redirectUrl = button.dataset.redirectTemplate.replace("CID", clientId)
    await this.#runDriveImport(url, files, formatId, redirectUrl)
  }

  async #runDriveImport(url, files, formatId, redirectUrl) {
    this.formContainerTarget.classList.add("hidden")
    this.progressContainerTarget.classList.remove("hidden")

    if (this.hasAggregateCountsTarget) {
      this.aggregateCountsTarget.classList.remove("hidden")
      this.aggregateCountsTarget.classList.add("flex")
    }

    const total = files.length
    let successCount = 0
    let duplicateCount = 0
    let failedCount = 0
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      this.progressTextTarget.textContent = this.uploadingTemplateValue
        .replace("{current}", i + 1)
        .replace("{total}", total)
      this.progressBarTarget.style.width = `${Math.round((i / total) * 100)}%`

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "X-CSRF-Token": csrfToken,
            "Accept": "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ file_id: file.id, name: file.name, statement_format_id: formatId }),
        })

        const result = await response.json()

        if (result.status === "ok") {
          successCount++
          this.#appendCard(result.upload_progress_html)
        } else if (result.status === "duplicate") {
          duplicateCount++
          this.#appendCard(result.upload_progress_html)
        } else {
          failedCount++
        }
      } catch {
        failedCount++
      }

      this.successCountTarget.textContent = this.successTemplateValue.replace("{count}", successCount)
      this.duplicateCountTarget.textContent = this.duplicateTemplateValue.replace("{count}", duplicateCount)
      this.failedCountTarget.textContent = this.failedTemplateValue.replace("{count}", failedCount)
    }

    this.progressBarTarget.style.width = "100%"
    this.progressTextTarget.textContent = this.completeTextValue

    if (redirectUrl) {
      setTimeout(() => {
        window.location.href = redirectUrl
      }, 1500)
    }
  }

  async #runBatchUpload(form, files) {
    const isSingleFile = files.length === 1 && !this.#isZip(files[0])

    this.formContainerTarget.classList.add("hidden")
    this.progressContainerTarget.classList.remove("hidden")

    if (!isSingleFile && this.hasAggregateCountsTarget) {
      this.aggregateCountsTarget.classList.remove("hidden")
      this.aggregateCountsTarget.classList.add("flex")
    }

    const total = files.length
    let successCount = 0
    let duplicateCount = 0
    let failedCount = 0

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const formData = new FormData(form)

    for (let i = 0; i < files.length; i++) {
      const currentFile = files[i]
      const isZip = this.#isZip(currentFile)

      if (isZip) {
        this.progressTextTarget.textContent = this.unzippingTemplateValue
          .replace("{filename}", currentFile.name)
      } else {
        this.progressTextTarget.textContent = this.uploadingTemplateValue
          .replace("{current}", i + 1)
          .replace("{total}", total)
      }
      this.progressBarTarget.style.width = `${Math.round((i / total) * 100)}%`

      const singleFormData = new FormData()
      for (const [key, value] of formData.entries()) {
        if (key !== "statement[files][]" && key !== "statement[file]") {
          singleFormData.append(key, value)
        }
      }
      singleFormData.append("statement[file]", currentFile)
      if (this.extractionMode) {
        singleFormData.append("statement[extraction_mode]", this.extractionMode)
      }

      try {
        const response = await fetch(this.uploadUrlValue, {
          method: "POST",
          headers: {
            "X-CSRF-Token": csrfToken,
            "Accept": "application/json",
          },
          body: singleFormData,
        })

        const result = await response.json()

        if (result.status === "batch" && Array.isArray(result.entries)) {
          for (const entry of result.entries) {
            if (entry.status === "ok") successCount++
            else if (entry.status === "duplicate") duplicateCount++
            else failedCount++
            this.#appendCard(entry.upload_progress_html)
          }
        } else if (result.status === "ok") {
          successCount++
          this.#appendCard(result.upload_progress_html)
        } else if (result.status === "duplicate") {
          duplicateCount++
          this.#appendCard(result.upload_progress_html)
        } else {
          failedCount++
        }
      } catch {
        failedCount++
      }

      this.successCountTarget.textContent = this.successTemplateValue.replace("{count}", successCount)
      this.duplicateCountTarget.textContent = this.duplicateTemplateValue.replace("{count}", duplicateCount)
      this.failedCountTarget.textContent = this.failedTemplateValue.replace("{count}", failedCount)
    }

    this.progressBarTarget.style.width = "100%"
    this.progressTextTarget.textContent = this.completeTextValue

    // Single-file cards carry their own redirect URL (baked in server-side) and
    // navigate themselves once processing completes. Batch uploads redirect once here.
    if (!isSingleFile && this.redirectUrlValue) {
      setTimeout(() => {
        window.location.href = this.redirectUrlValue
      }, 1500)
    }
  }

  #appendCard(html) {
    if (!html || !this.hasCardListTarget) return
    const template = document.createElement("template")
    template.innerHTML = html.trim()
    this.cardListTarget.appendChild(template.content)
  }

  #isZip(file) {
    return file && typeof file.name === "string" && file.name.toLowerCase().endsWith(".zip")
  }
}
